/**
 * Wizard Pricing Module
 * Handles pricing calculations for wizard-based services
 *
 * PRICING: Now dynamically loaded from Settings service via shared package
 * Fallback: Hardcoded values retained for rollback if needed
 */

import { getPricingConfig } from '../../shared/src/config/pricing.js';

// Dynamic pricing data (loaded from database)
let dynamicPricing = null;

/**
 * Initialize pricing from database
 * Call this when the wizard page loads
 */
export async function initializeBillingPricing() {
    try {
        const pricing = await getPricingConfig();
        dynamicPricing = pricing;
        console.log('✅ Dynamic pricing loaded from Settings service (Billing)');

        // Update window.serviceData with new rates
        if (window.serviceData) {
            window.serviceData.recurring_cleaning.rate = getRate('recurring_cleaning_rate', FALLBACK_RATES.RECURRING_CLEANING);
            window.serviceData.onetime_cleaning.rate = getRate('onetime_cleaning_rate', FALLBACK_RATES.ONETIME_CLEANING);
            window.serviceData.underwater_inspection.rate = getRate('underwater_inspection_rate', FALLBACK_RATES.UNDERWATER_INSPECTION);
            window.serviceData.item_recovery.rate = getRate('item_recovery_rate', FALLBACK_RATES.ITEM_RECOVERY);
            window.serviceData.propeller_service.rate = getRate('propeller_service_rate', FALLBACK_RATES.PROPELLER_SERVICE);
            window.serviceData.anodes_only.rate = getRate('anodes_only_rate', FALLBACK_RATES.ANODES_ONLY);
        }

        return pricing;
    } catch (error) {
        console.error('❌ Failed to load dynamic pricing, using fallback:', error);
        return null;
    }
}

/**
 * Get current pricing (dynamic or fallback)
 */
function getRate(key, fallback) {
    return dynamicPricing?.[key] ?? fallback;
}

function getSurcharge(key, fallback) {
    // Database stores surcharges as percentages (25.00 = 25%)
    // Convert to decimal for calculations (25.00 → 0.25)
    const dbValue = dynamicPricing?.[key];
    return dbValue !== undefined ? dbValue / 100 : fallback;
}

// HARDCODED FALLBACK VALUES (for rollback if needed)
const FALLBACK_RATES = {
    RECURRING_CLEANING: 4.5,
    ONETIME_CLEANING: 6,
    UNDERWATER_INSPECTION: 4,
    ITEM_RECOVERY: 199,
    PROPELLER_SERVICE: 349,
    ANODES_ONLY: 150,
    MINIMUM_SERVICE_FEE: 150
};

const FALLBACK_SURCHARGES = {
    POWERBOAT: 0.25,
    CATAMARAN: 0.25,
    TRIMARAN: 0.5,
    TWIN_ENGINES: 0.1,
    ADDITIONAL_PROPELLER: 0.1,
    // Paint surcharges REMOVED - poor paint → heavier growth → growth surcharge covers it
    POOR_PAINT: 0,              // Was 0.1 - now 0
    MISSING_PAINT: 0,           // Was 0.15 - now 0
    // Growth surcharges aligned with hull-fouling-matrix.json v2.0
    // Surcharges start at Moderate-Heavy (25%), not Moderate
    MODERATE_GROWTH: 0,            // Moderate: 0% (no surcharge)
    MODERATE_HEAVY_GROWTH: 0.25,   // Moderate-Heavy: 25% (first surcharge)
    HEAVY_GROWTH: 0.50,            // Heavy: 50%
    HEAVY_SEVERE_GROWTH: 0.75,     // Heavy-Severe: 75%
    SEVERE_GROWTH: 1.00,           // Severe: 100%
    SEVERE_MAX_GROWTH: 2.00        // Severe (Maximum): 200%
};

// Pricing constants (with dynamic loading)
const RATES = {
    get RECURRING_CLEANING() { return getRate('recurring_cleaning_rate', FALLBACK_RATES.RECURRING_CLEANING); },
    get ONETIME_CLEANING() { return getRate('onetime_cleaning_rate', FALLBACK_RATES.ONETIME_CLEANING); },
    get UNDERWATER_INSPECTION() { return getRate('underwater_inspection_rate', FALLBACK_RATES.UNDERWATER_INSPECTION); },
    get ITEM_RECOVERY() { return getRate('item_recovery_rate', FALLBACK_RATES.ITEM_RECOVERY); },
    get PROPELLER_SERVICE() { return getRate('propeller_service_rate', FALLBACK_RATES.PROPELLER_SERVICE); },
    get ANODES_ONLY() { return getRate('anodes_only_rate', FALLBACK_RATES.ANODES_ONLY); }
};

const SURCHARGES = {
    get POWERBOAT() { return getSurcharge('surcharge_powerboat', FALLBACK_SURCHARGES.POWERBOAT); },
    get CATAMARAN() { return getSurcharge('surcharge_catamaran', FALLBACK_SURCHARGES.CATAMARAN); },
    get TRIMARAN() { return getSurcharge('surcharge_trimaran', FALLBACK_SURCHARGES.TRIMARAN); },
    TWIN_ENGINES: FALLBACK_SURCHARGES.TWIN_ENGINES, // Not in database, keep hardcoded
    get ADDITIONAL_PROPELLER() { return getSurcharge('surcharge_additional_propeller', FALLBACK_SURCHARGES.ADDITIONAL_PROPELLER); },
    get POOR_PAINT() { return getSurcharge('surcharge_paint_poor', FALLBACK_SURCHARGES.POOR_PAINT); },
    get MISSING_PAINT() { return getSurcharge('surcharge_paint_missing', FALLBACK_SURCHARGES.MISSING_PAINT); },
    // Growth surcharges aligned with hull-fouling-matrix.json v2.0 (surcharges start at M-H)
    get MODERATE_GROWTH() { return getSurcharge('surcharge_moderate_growth', FALLBACK_SURCHARGES.MODERATE_GROWTH); },
    get MODERATE_HEAVY_GROWTH() { return getSurcharge('surcharge_moderate_heavy_growth', FALLBACK_SURCHARGES.MODERATE_HEAVY_GROWTH); },
    get HEAVY_GROWTH() { return getSurcharge('surcharge_heavy_growth', FALLBACK_SURCHARGES.HEAVY_GROWTH); },
    get HEAVY_SEVERE_GROWTH() { return getSurcharge('surcharge_heavy_severe_growth', FALLBACK_SURCHARGES.HEAVY_SEVERE_GROWTH); },
    get SEVERE_GROWTH() { return getSurcharge('surcharge_severe_growth', FALLBACK_SURCHARGES.SEVERE_GROWTH); },
    get SEVERE_MAX_GROWTH() { return getSurcharge('surcharge_severe_max_growth', FALLBACK_SURCHARGES.SEVERE_MAX_GROWTH); }
};

const MINIMUM_SERVICE_FEE = () => getRate('minimum_service_charge', FALLBACK_RATES.MINIMUM_SERVICE_FEE);

// Initialize adjustment state
if (!window.priceAdjustment) {
    window.priceAdjustment = {
        type: 'none', // 'none', 'percent_discount', 'dollar_discount', 'set_total'
        value: 0
    };
}

// Initialize service data
if (!window.serviceData) {
    window.serviceData = {
        recurring_cleaning: {
            rate: RATES.RECURRING_CLEANING,
            name: "Recurring Cleaning & Anodes",
            type: "per_foot",
            description: "Regular hull cleaning keeps your boat performing at its best. Service includes cleaning and zinc anode inspection. Available at 1, 2, 3, or 6-month intervals."
        },
        onetime_cleaning: {
            rate: RATES.ONETIME_CLEANING,
            name: "One-time Cleaning & Anodes",
            type: "per_foot",
            description: "Complete hull cleaning and zinc anode inspection. Perfect for pre-haul out, pre-survey, or when your regular diver is unavailable."
        },
        item_recovery: {
            rate: RATES.ITEM_RECOVERY,
            name: "Item Recovery",
            type: "flat",
            description: "Professional recovery of lost items like phones, keys, tools, or dinghies. Quick response to minimize water damage. Service includes up to 45 minutes of searching time. Recovery is not guaranteed."
        },
        underwater_inspection: {
            rate: RATES.UNDERWATER_INSPECTION,
            name: "Underwater Inspection",
            type: "per_foot",
            description: `Thorough underwater inspection with detailed photo/video documentation. Ideal for insurance claims, pre-purchase surveys, or damage assessment. $${RATES.UNDERWATER_INSPECTION} per foot with $${MINIMUM_SERVICE_FEE()} minimum.`
        },
        propeller_service: {
            rate: RATES.PROPELLER_SERVICE,
            name: "Propeller Removal/Installation",
            type: "flat",
            description: `Professional propeller removal or installation service. $${RATES.PROPELLER_SERVICE} per propeller for either service. Includes proper handling, alignment, and torque specifications.`
        },
        anodes_only: {
            rate: RATES.ANODES_ONLY,
            name: "Anodes Only",
            type: "flat",
            description: `Zinc anode inspection and replacement service. $${MINIMUM_SERVICE_FEE()} minimum service charge plus cost of anodes. Perfect for boats that only need anode replacement without hull cleaning.`
        }
    };
    console.log('✅ Service data initialized');
}

// Flag to prevent infinite loops
let isRunning = false;

/**
 * Update wizard pricing calculation and charge summary
 */
export function updateWizardPricing() {
    if (isRunning) {
        console.log('updateWizardPricing already running, skipping to prevent infinite loop');
        return;
    }

    isRunning = true;

    // Save current focus and scroll position
    const activeElement = document.activeElement;
    const isAdjustmentInput = activeElement?.id === 'adjustmentValue';
    const cursorPosition = isAdjustmentInput ? activeElement.selectionStart : null;
    const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

    try {
        const serviceKey = window.currentServiceKey || window.selectedServiceKey;
        if (!serviceKey) {
            console.warn('No service selected');
            return;
        }

        const serviceData = window.serviceData && window.serviceData[serviceKey];
        if (!serviceData) {
            console.error('Service data not found for:', serviceKey);
            console.log('Available serviceData:', window.serviceData);
            return;
        }

        // Get boat length - try multiple sources, prioritizing modal input (user override)
        let boatLength = 0;

        // Check modal input FIRST - this allows user to override stored values
        const modalBoatLengthInput = document.getElementById('modalBoatLength');
        if (modalBoatLengthInput && modalBoatLengthInput.value) {
            boatLength = parseFloat(modalBoatLengthInput.value);
        }

        // If no modal input, fall back to selected customer/boat
        if (!boatLength || boatLength === 0) {
            if (window.selectedCustomer?.boat_length) {
                boatLength = parseFloat(window.selectedCustomer.boat_length);
            } else if (window.selectedBoat?.length) {
                boatLength = parseFloat(window.selectedBoat.length);
            }
        }

        // Fall back to other form inputs
        if (!boatLength || boatLength === 0) {
            boatLength = parseFloat(document.getElementById('boatLength')?.value);
        }
        if (!boatLength || boatLength === 0) {
            boatLength = parseFloat(document.getElementById('wizardBoatLength')?.value);
        }
        if (!boatLength || boatLength === 0) {
            boatLength = 35; // Default fallback
        }
        const isPerFoot = serviceData.type === 'per_foot';

        // Check minimum fee checkbox state for anodes_only service
        const chargeMinFeeCheckbox = document.getElementById('chargeMinimumFee');
        const shouldChargeMinFee = !chargeMinFeeCheckbox || chargeMinFeeCheckbox.checked;

        // Calculate base price
        let basePrice;
        if (serviceKey === 'anodes_only' && !shouldChargeMinFee) {
            // If anodes_only and checkbox unchecked, no base service fee
            basePrice = 0;
        } else {
            basePrice = isPerFoot ? serviceData.rate * boatLength : serviceData.rate;
        }

        let breakdownLines = [];
        let totalSurcharges = 0;

        // Base price breakdown
        if (isPerFoot && boatLength > 0) {
            breakdownLines.push(`Base rate: $${serviceData.rate}/ft × ${boatLength}ft = $${basePrice.toFixed(2)}`);
        } else if (!isPerFoot && basePrice > 0) {
            breakdownLines.push(`Flat rate: $${basePrice.toFixed(2)}`);
        }

        // Only add surcharges for cleaning services
        if (serviceKey === 'recurring_cleaning' || serviceKey === 'onetime_cleaning') {
            // Powerboat surcharge - prioritize window.selectedBoat over DOM
            const boatType = window.selectedBoat?.type ||
                            document.querySelector('.boat-type-btn.selected')?.getAttribute('data-value') ||
                            document.querySelector('input[name="wizard_boat_type"]:checked')?.value ||
                            document.querySelector('input[name="boat_type"]:checked')?.value ||
                            'sailboat';

            console.log('Boat type for pricing:', boatType);

            if (boatType === 'powerboat') {
                const surcharge = basePrice * SURCHARGES.POWERBOAT;
                totalSurcharges += surcharge;
                breakdownLines.push(`Powerboat surcharge (${SURCHARGES.POWERBOAT * 100}%): +$${surcharge.toFixed(2)}`);
            }

            // Hull type surcharge - prioritize window.selectedBoat over DOM
            const hullType = window.selectedBoat?.hull_type ||
                            document.querySelector('.hull-type-btn.selected')?.getAttribute('data-value') ||
                            document.querySelector('input[name="wizard_hull_type"]:checked')?.value ||
                            document.querySelector('input[name="hull_type"]:checked')?.value ||
                            'monohull';

            console.log('Hull type for pricing:', hullType);

            if (hullType === 'catamaran') {
                const surcharge = basePrice * SURCHARGES.CATAMARAN;
                totalSurcharges += surcharge;
                breakdownLines.push(`Catamaran surcharge (${SURCHARGES.CATAMARAN * 100}%): +$${surcharge.toFixed(2)}`);
            } else if (hullType === 'trimaran') {
                const surcharge = basePrice * SURCHARGES.TRIMARAN;
                totalSurcharges += surcharge;
                breakdownLines.push(`Trimaran surcharge (${SURCHARGES.TRIMARAN * 100}%): +$${surcharge.toFixed(2)}`);
            }

            // Additional propeller surcharge (10% per propeller beyond first)
            const propellerCount = parseInt(document.getElementById('wizardPropellerCount')?.value || window.selectedPropellerCount || window.selectedBoat?.propeller_count || 0);
            if (propellerCount > 1) {
                const additionalPropellers = propellerCount - 1;
                const surcharge = basePrice * SURCHARGES.ADDITIONAL_PROPELLER * additionalPropellers;
                totalSurcharges += surcharge;
                const totalPercent = (SURCHARGES.ADDITIONAL_PROPELLER * additionalPropellers * 100).toFixed(0);
                breakdownLines.push(`Additional propellers (${additionalPropellers} × 10%): +$${surcharge.toFixed(2)}`);
            }

            // NOTE: Paint condition is tracked for records but no surcharge is applied.
            // Rationale: Poor paint → heavier growth → growth surcharge already covers the economic impact.
            // Charging for both paint AND growth would be double-dipping.

            // Growth level surcharge
            const actualGrowthSlider = document.getElementById('actualGrowthLevel');
            const wizardGrowthElement = document.getElementById('wizardGrowthLevel');
            const growthLevelElement = document.getElementById('growth_level');

            let growthLevel = 'minimal';
            let growthMultiplier = 0;

            // If actualGrowthLevel slider exists, map its numeric value
            if (actualGrowthSlider) {
                const sliderValue = parseInt(actualGrowthSlider.value);
                // Slider values aligned with hull-fouling-matrix.json v2.0
                // Surcharges start at Moderate-Heavy (25%), not Moderate
                // 0=Not Inspected, 1=Minimal, 2=Minimal-Moderate, 3=Moderate, 4=Moderate-Heavy, 5=Heavy, 6=Heavy-Severe, 7=Severe, 8=Severe (Maximum)
                const growthLevelMap = [
                    'not_inspected',      // 0: 0%
                    'minimal',            // 1: 0%
                    'minimal-moderate',   // 2: 0%
                    'moderate',           // 3: 0%
                    'moderate-heavy',     // 4: 25% (first surcharge)
                    'heavy',              // 5: 50%
                    'heavy-severe',       // 6: 75%
                    'severe',             // 7: 100%
                    'severe-maximum'      // 8: 200%
                ];
                const surchargeMap = [
                    0,                                      // 0: not inspected
                    0,                                      // 1: minimal
                    0,                                      // 2: minimal-moderate
                    SURCHARGES.MODERATE_GROWTH,             // 3: moderate (0%)
                    SURCHARGES.MODERATE_HEAVY_GROWTH,       // 4: moderate-heavy (25%)
                    SURCHARGES.HEAVY_GROWTH,                // 5: heavy (50%)
                    SURCHARGES.HEAVY_SEVERE_GROWTH,         // 6: heavy-severe (75%)
                    SURCHARGES.SEVERE_GROWTH,               // 7: severe (100%)
                    SURCHARGES.SEVERE_MAX_GROWTH            // 8: severe-maximum (200%)
                ];

                growthLevel = growthLevelMap[sliderValue] || 'minimal';
                growthMultiplier = surchargeMap[sliderValue] || 0;

                // Check if data-value attribute exists for more precise mapping
                if (actualGrowthSlider.hasAttribute('data-value')) {
                    growthLevel = actualGrowthSlider.getAttribute('data-value');
                }

                console.log(`Growth slider value: ${sliderValue} -> ${growthLevel} -> ${(growthMultiplier * 100)}% surcharge`);

            } else {
                // Try wizard or hidden field
                const growthElement = wizardGrowthElement || growthLevelElement;
                growthLevel = growthElement?.value || growthElement?.getAttribute('data-value') || 'minimal';

                // Check for data-surcharge attribute (set by conditions-tracking.js)
                if (growthElement && growthElement.hasAttribute('data-surcharge')) {
                    growthMultiplier = parseFloat(growthElement.getAttribute('data-surcharge')) || 0;
                } else {
                    // Aligned with hull-fouling-matrix.json v2.0 (surcharges start at M-H)
                    const growthSurcharges = {
                        minimal: 0,
                        'minimal-moderate': 0,
                        moderate: SURCHARGES.MODERATE_GROWTH,                // 0%
                        'moderate-heavy': SURCHARGES.MODERATE_HEAVY_GROWTH,  // 25% (first surcharge)
                        heavy: SURCHARGES.HEAVY_GROWTH,                      // 50%
                        'heavy-severe': SURCHARGES.HEAVY_SEVERE_GROWTH,      // 75%
                        severe: SURCHARGES.SEVERE_GROWTH,                    // 100%
                        'severe-maximum': SURCHARGES.SEVERE_MAX_GROWTH       // 200%
                    };
                    growthMultiplier = growthSurcharges[growthLevel] || 0;
                }
            }

            if (growthMultiplier > 0) {
                const surcharge = basePrice * growthMultiplier;
                totalSurcharges += surcharge;
                const percent = (growthMultiplier * 100).toFixed(0);
                const displayLevel = growthLevel.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                breakdownLines.push(`Growth level (${displayLevel}) surcharge (${percent}%): +$${surcharge.toFixed(2)}`);
            }
        }

        // Total surcharges line removed - individual surcharges are shown above

        // Calculate subtotal
        let subtotal = basePrice + totalSurcharges;

        // Apply minimum service fee if needed
        let finalPrice;
        if (shouldChargeMinFee) {
            const minFee = MINIMUM_SERVICE_FEE();
            finalPrice = Math.max(subtotal, minFee);
            if (finalPrice === minFee && subtotal < minFee) {
                breakdownLines.push(`Minimum charge applied: $${minFee}`);
            }
        } else {
            // No minimum fee - just use subtotal (for anodes_only when unchecked)
            finalPrice = subtotal;
        }

        // Update charge summary content
        const chargeSummaryContent = document.getElementById('chargeSummaryContent');
        if (chargeSummaryContent) {
            let html = '<div class="charge-breakdown">';

            // === CUSTOMER INFORMATION ===
            const customerName = document.getElementById('wizardCustomerName')?.value || window.selectedCustomer?.name;
            const customerEmail = document.getElementById('wizardCustomerEmail')?.value || window.selectedCustomer?.email;
            const customerPhone = document.getElementById('wizardCustomerPhone')?.value || window.selectedCustomer?.phone;

            if (customerName || customerEmail) {
                html += '<div style="margin-bottom: 15px; padding-bottom: 10px; border-bottom: 2px solid #3498db;">';
                html += '<h4 style="margin: 0 0 8px 0; color: #2c3e50; font-size: 16px;">👤 Customer</h4>';
                if (customerName) {
                    html += `<div style="font-size: 14px; margin: 3px 0;"><strong>Name:</strong> ${customerName}</div>`;
                }
                if (customerEmail) {
                    html += `<div style="font-size: 14px; margin: 3px 0;"><strong>Email:</strong> ${customerEmail}</div>`;
                }
                if (customerPhone) {
                    html += `<div style="font-size: 14px; margin: 3px 0;"><strong>Phone:</strong> ${customerPhone}</div>`;
                }
                html += '</div>';
            }

            // === BOAT INFORMATION ===
            const boatName = document.getElementById('wizardBoatName')?.value || window.selectedCustomer?.boat_name || window.selectedBoat?.name;
            // Only show boat section if customer is selected (don't show default values)
            const hasCustomer = window.selectedCustomer || window.selectedBoat;
            if (hasCustomer && (boatName || boatLength > 0)) {
                html += '<div style="margin-bottom: 15px; padding-bottom: 10px; border-bottom: 2px solid #3498db;">';
                html += '<h4 style="margin: 0 0 8px 0; color: #2c3e50; font-size: 16px;">⛵ Boat</h4>';
                if (boatName) {
                    html += `<div style="font-size: 14px; margin: 3px 0;"><strong>Name:</strong> ${boatName}</div>`;
                }
                if (boatLength > 0) {
                    html += `<div style="font-size: 14px; margin: 3px 0;"><strong>Length:</strong> ${boatLength} feet</div>`;
                }

                // Boat configuration
                const boatTypeChecked = document.querySelector('.boat-type-btn.selected')?.getAttribute('data-value') ||
                                       document.querySelector('input[name="wizard_boat_type"]:checked')?.value;
                const hullTypeChecked = document.querySelector('.hull-type-btn.selected')?.getAttribute('data-value') ||
                                       document.querySelector('input[name="wizard_hull_type"]:checked')?.value;

                if (boatTypeChecked) {
                    const boatTypeLabel = boatTypeChecked === 'powerboat' ? 'Powerboat' : 'Sailboat';
                    html += `<div style="font-size: 14px; margin: 3px 0;"><strong>Type:</strong> ${boatTypeLabel}</div>`;
                }
                if (hullTypeChecked) {
                    const hullTypeLabel = hullTypeChecked === 'catamaran' ? 'Catamaran' :
                                         hullTypeChecked === 'trimaran' ? 'Trimaran' : 'Monohull';
                    html += `<div style="font-size: 14px; margin: 3px 0;"><strong>Hull:</strong> ${hullTypeLabel}</div>`;
                }

                // Propeller Count (moved from propeller section)
                const propellerCount = parseInt(document.getElementById('wizardPropellerCount')?.value || window.selectedPropellerCount || window.selectedBoat?.propeller_count || 0);
                if (propellerCount >= 0) {
                    html += `<div style="font-size: 14px; margin: 3px 0;"><strong>Propellers:</strong> ${propellerCount} ${propellerCount === 1 ? 'propeller' : 'propellers'}</div>`;
                }

                // Anode Locations (extract unique locations from assignments)
                if (window.anodeAssignments && Object.keys(window.anodeAssignments).length > 0) {
                    const anodeLocations = {};
                    Object.entries(window.anodeAssignments).forEach(([key, anode]) => {
                        const loc = anode.location;
                        if (!anodeLocations[loc]) {
                            anodeLocations[loc] = 0;
                        }
                        anodeLocations[loc]++;
                    });

                    const locationsList = Object.entries(anodeLocations)
                        .map(([loc, count]) => {
                            const locLabel = loc.charAt(0).toUpperCase() + loc.slice(1);
                            return count > 1 ? `${locLabel} (${count})` : locLabel;
                        })
                        .join(', ');

                    if (locationsList) {
                        html += `<div style="font-size: 14px; margin: 3px 0;"><strong>Anode Locations:</strong> ${locationsList}</div>`;
                    }
                }

                html += '</div>';
            }

            // === SERVICE INFORMATION ===
            html += '<div style="margin-bottom: 15px; padding-bottom: 10px; border-bottom: 2px solid #3498db;">';
            html += '<h4 style="margin: 0 0 8px 0; color: #2c3e50; font-size: 16px;">🔧 Service</h4>';
            html += `<div style="font-size: 14px; margin: 3px 0;"><strong>Type:</strong> ${serviceData.name}</div>`;

            // Show conditions for cleaning services
            if (serviceKey === 'recurring_cleaning' || serviceKey === 'onetime_cleaning') {
                const paintSlider = document.getElementById('actualPaintCondition');
                const growthSlider = document.getElementById('actualGrowthLevel');

                if (paintSlider) {
                    const paintValue = parseInt(paintSlider.value);
                    // Labels must match conditions-tracking.js 10-value scale (0-9)
                    const paintLabels = ['Not Inspected', 'Excellent', 'Exc-Good', 'Good', 'Good-Fair', 'Fair', 'Fair-Poor', 'Poor', 'Poor-Missing', 'Missing'];
                    const paintLabel = paintLabels[paintValue] || 'Not Inspected';
                    html += `<div style="font-size: 14px; margin: 3px 0;"><strong>Paint Condition:</strong> ${paintLabel}</div>`;
                }

                if (growthSlider) {
                    const growthValue = parseInt(growthSlider.value);
                    // Aligned with hull-fouling-matrix.json (0-200% scale)
                    const growthLabels = [
                        'Not Inspected',     // 0: 0%
                        'Minimal',           // 1: 0%
                        'Minimal-Moderate',  // 2: 0%
                        'Moderate',          // 3: 25%
                        'Moderate-Heavy',    // 4: 50%
                        'Heavy',             // 5: 75%
                        'Heavy-Severe',      // 6: 100%
                        'Severe',            // 7: 150%
                        'Severe (Maximum)'   // 8: 200%
                    ];
                    const growthLabel = growthLabels[growthValue] || 'Not Inspected';
                    html += `<div style="font-size: 14px; margin: 3px 0;"><strong>Growth Level:</strong> ${growthLabel}</div>`;
                }
            }

            // Show thru-hull condition
            const thruHullCondition = document.getElementById('wizardThruHull')?.value;
            if (thruHullCondition) {
                const thruHullLabel = thruHullCondition === 'excellent' ? 'Excellent' :
                                     thruHullCondition === 'good' ? 'Good' :
                                     thruHullCondition === 'fair' ? 'Fair' :
                                     thruHullCondition === 'needs_attention' ? 'Needs Attention' : thruHullCondition;
                html += `<div style="font-size: 14px; margin: 3px 0;"><strong>Thru-Hull:</strong> ${thruHullLabel}</div>`;

                // Show thru-hull notes if available
                const thruHullNotes = document.getElementById('wizardThruHullNotes')?.value;
                if (thruHullNotes && thruHullNotes.trim()) {
                    html += `<div style="font-size: 13px; margin: 3px 0 3px 20px; color: #666; font-style: italic;">${thruHullNotes}</div>`;
                }
            }

            // Show propeller conditions
            const prop1Condition = document.getElementById('wizardPropeller1')?.value;
            const prop2Condition = document.getElementById('wizardPropeller2')?.value;
            const propellerCount = parseInt(document.getElementById('wizardPropellerCount')?.value || window.selectedPropellerCount || window.selectedBoat?.propeller_count || 0);

            if (prop1Condition) {
                const prop1Label = prop1Condition.charAt(0).toUpperCase() + prop1Condition.slice(1);
                html += `<div style="font-size: 14px; margin: 3px 0;"><strong>Propeller 1:</strong> ${prop1Label}</div>`;
            }

            if (prop2Condition && propellerCount >= 2) {
                const prop2Label = prop2Condition.charAt(0).toUpperCase() + prop2Condition.slice(1);
                html += `<div style="font-size: 14px; margin: 3px 0;"><strong>Propeller 2:</strong> ${prop2Label}</div>`;
            }

            // Show propeller notes if available
            const propellerNotes = document.getElementById('wizardPropellerNotes')?.value;
            if (propellerNotes && propellerNotes.trim()) {
                html += `<div style="font-size: 13px; margin: 3px 0 3px 20px; color: #666; font-style: italic;">${propellerNotes}</div>`;
            }

            // Show general service notes if available
            const serviceNotes = document.getElementById('wizardGeneralNotes')?.value;
            if (serviceNotes && serviceNotes.trim()) {
                html += `<div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #ddd;">`;
                html += `<div style="font-size: 14px; margin: 3px 0;"><strong>Service Notes:</strong></div>`;
                html += `<div style="font-size: 13px; margin: 3px 0; color: #666; font-style: italic;">${serviceNotes}</div>`;
                html += `</div>`;
            }

            // === ANODES CONDITIONS (Subsection of Service) ===
            let anodeTotalCost = 0;
            const laborPerAnode = 15; // $15 labor per anode
            const allAnodes = [];

            if (window.anodeAssignments && Object.keys(window.anodeAssignments).length > 0) {
                Object.entries(window.anodeAssignments).forEach(([key, anode]) => {
                    allAnodes.push({
                        location: anode.location,
                        position: anode.position,
                        condition: anode.condition !== undefined && anode.condition !== null ? anode.condition : 100,
                        status: anode.status || 'inspected',
                        name: anode.anode_name || 'Not Selected',
                        price: parseFloat(anode.anode_price) || 0
                    });

                    // Only add costs for replaced anodes (not retrieve or order)
                    // retrieve = retrieve from stock (no charge, triggers reminder)
                    // order = out of stock (no charge, triggers inventory order)
                    if (anode.status === 'replaced' && anode.anode_name && anode.anode_price) {
                        anodeTotalCost += (parseFloat(anode.anode_price) || 0) + laborPerAnode;
                    }
                });
            }

            if (allAnodes.length > 0) {
                // Anodes as subsection (no separate border/section)
                html += '<div style="margin-top: 10px; padding-top: 8px; border-top: 1px solid #ddd;">';
                html += '<h5 style="margin: 0 0 8px 0; color: #34495e; font-size: 14px; font-weight: 600;">⚓ Anodes Conditions</h5>';

                allAnodes.forEach(anode => {
                    // Format location with proper title case
                    const locationLabel = anode.location.charAt(0).toUpperCase() + anode.location.slice(1);

                    // Format position with proper title case
                    const positionLabel = anode.position.charAt(0).toUpperCase() + anode.position.slice(1);

                    const conditionLabel = anode.condition === 0 ? 'Missing' : `${anode.condition}%`;
                    const statusLabel = anode.status.charAt(0).toUpperCase() + anode.status.slice(1);
                    const isReplaced = anode.status === 'replaced';

                    // Different background color for replaced anodes
                    const bgColor = isReplaced ? '#e8f5e9' : '#f8f9fa';

                    html += `
                        <div style="background: ${bgColor}; padding: 8px; margin: 5px 0; border-radius: 4px;">
                            <div style="font-size: 13px; font-weight: 600; margin-bottom: 3px;">
                                ${locationLabel} - ${positionLabel}
                            </div>
                            <div style="font-size: 12px; color: #666; margin-bottom: 3px;">
                                Condition: ${conditionLabel} | Status: ${statusLabel}
                            </div>
                    `;

                    // Show cost details only for replaced anodes
                    if (isReplaced && anode.name !== 'Not Selected') {
                        const anodeTotal = anode.price + laborPerAnode;
                        html += `
                            <div style="font-size: 12px; color: #333; margin-top: 5px; padding-top: 5px; border-top: 1px solid #ddd;">
                                ${anode.name}: $${anode.price.toFixed(2)} + Labor: $${laborPerAnode.toFixed(2)} = <strong>$${anodeTotal.toFixed(2)}</strong>
                            </div>
                        `;
                    } else if (anode.name !== 'Not Selected') {
                        html += `
                            <div style="font-size: 12px; color: #666; margin-top: 3px;">
                                ${anode.name} (no charge)
                            </div>
                        `;
                    }

                    html += `</div>`;
                });

                html += '</div>';
            }

            // Close SERVICE section
            html += '</div>';

            // Add anode costs to final price (only replaced anodes)
            finalPrice += anodeTotalCost;

            // Store unadjusted total for display
            const unadjustedTotal = finalPrice;

            // === APPLY ADJUSTMENTS ===
            let adjustmentAmount = 0;
            const adjustment = window.priceAdjustment || { type: 'none', value: 0 };

            if (adjustment.type === 'percent_discount' && adjustment.value > 0) {
                adjustmentAmount = (unadjustedTotal * adjustment.value) / 100;
                finalPrice = unadjustedTotal - adjustmentAmount;
            } else if (adjustment.type === 'dollar_discount' && adjustment.value > 0) {
                adjustmentAmount = adjustment.value;
                finalPrice = Math.max(0, unadjustedTotal - adjustmentAmount);
            } else if (adjustment.type === 'set_total' && adjustment.value >= 0) {
                adjustmentAmount = unadjustedTotal - adjustment.value;
                finalPrice = adjustment.value;
            }

            // === PRICING BREAKDOWN ===
            html += '<div style="margin-bottom: 15px;">';
            html += '<h4 style="margin: 0 0 8px 0; color: #2c3e50; font-size: 16px;">💰 Pricing</h4>';
            breakdownLines.forEach(line => {
                html += `<div style="font-size: 13px; margin: 3px 0; color: #555;">${line}</div>`;
            });

            // Add anodes subtotal if there are replaced anodes
            if (anodeTotalCost > 0) {
                html += `<div style="font-size: 13px; margin: 3px 0; color: #555;"><strong>Anodes Subtotal (Replaced Only): $${anodeTotalCost.toFixed(2)}</strong></div>`;
            }

            html += '</div>';

            // === ADJUSTMENT CONTROLS ===
            // Show inline adjustment info if active, otherwise show Edit button
            if (adjustment.type !== 'none' && adjustmentAmount !== 0) {
                // Show adjustment summary inline
                html += '<div style="margin-bottom: 10px; padding: 10px; background: #fff3cd; border: 1px solid #ffc107; border-radius: 4px;">';
                html += '<div style="display: flex; justify-content: space-between; align-items: center;">';
                html += '<div style="font-size: 13px; color: #856404;">';

                if (adjustment.type === 'percent_discount') {
                    html += `<strong>Discount (${adjustment.value}%):</strong> -$${adjustmentAmount.toFixed(2)}`;
                } else if (adjustment.type === 'dollar_discount') {
                    html += `<strong>Discount:</strong> -$${adjustmentAmount.toFixed(2)}`;
                } else if (adjustment.type === 'set_total') {
                    const sign = adjustmentAmount >= 0 ? '-' : '+';
                    html += `<strong>Adjustment:</strong> ${sign}$${Math.abs(adjustmentAmount).toFixed(2)}`;
                }

                html += '</div>';
                html += '<button onclick="window.toggleAdjustmentControls()" style="padding: 4px 10px; font-size: 12px; background: #3498db; color: white; border: none; border-radius: 4px; cursor: pointer;">Edit</button>';
                html += '</div>';
                html += '</div>';
            }

            // Adjustment controls panel (hidden by default, shown when Edit is clicked)
            const panelDisplay = (typeof window.showAdjustmentPanel !== 'undefined' && window.showAdjustmentPanel) ? 'block' : 'none';

            html += `<div id="adjustmentControlsPanel" style="display: ${panelDisplay}; margin-bottom: 15px; padding: 15px; background: #f0f8ff; border: 2px solid #3498db; border-radius: 8px;">`;
            html += '<div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">';
            html += '<h4 style="margin: 0; color: #2c3e50; font-size: 16px;">✏️ Adjust Price</h4>';
            html += '<button onclick="window.toggleAdjustmentControls()" style="padding: 4px 8px; font-size: 12px; background: #95a5a6; color: white; border: none; border-radius: 4px; cursor: pointer;">Close</button>';
            html += '</div>';

            // Adjustment type selector
            html += '<div style="margin-bottom: 10px;">';
            html += '<label style="display: block; font-size: 14px; font-weight: 600; margin-bottom: 5px; color: #2c3e50;">Adjustment Type:</label>';
            html += '<select id="adjustmentType" onchange="window.updateAdjustmentType(this.value)" style="width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 4px; font-size: 14px;">';
            html += `<option value="none" ${adjustment.type === 'none' ? 'selected' : ''}>No Adjustment</option>`;
            html += `<option value="percent_discount" ${adjustment.type === 'percent_discount' ? 'selected' : ''}>Percent Discount (%)</option>`;
            html += `<option value="dollar_discount" ${adjustment.type === 'dollar_discount' ? 'selected' : ''}>Dollar Discount ($)</option>`;
            html += `<option value="set_total" ${adjustment.type === 'set_total' ? 'selected' : ''}>Set Total Amount ($)</option>`;
            html += '</select>';
            html += '</div>';

            // Adjustment value input
            const isDisabled = adjustment.type === 'none';
            const placeholderText = adjustment.type === 'percent_discount' ? 'Enter discount percentage' :
                                   adjustment.type === 'dollar_discount' ? 'Enter discount amount' :
                                   adjustment.type === 'set_total' ? 'Enter final total' : '';

            html += '<div style="margin-bottom: 10px;">';
            html += '<label style="display: block; font-size: 14px; font-weight: 600; margin-bottom: 5px; color: #2c3e50;">Value:</label>';
            html += `<input type="text" id="adjustmentValue" value="${adjustment.value}"
                     oninput="window.updateAdjustmentValue(parseFloat(this.value) || 0)"
                     placeholder="${placeholderText}"
                     ${isDisabled ? 'disabled' : ''}
                     inputmode="decimal"
                     pattern="[0-9]*\\.?[0-9]*"
                     style="width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 4px; font-size: 14px; ${isDisabled ? 'background: #f5f5f5;' : ''}">`;
            html += '</div>';

            // Show adjustment preview if active
            if (adjustment.type !== 'none' && adjustmentAmount !== 0) {
                html += '<div style="margin-top: 10px; padding: 10px; background: #fff; border: 1px solid #3498db; border-radius: 4px;">';
                html += '<div style="font-size: 13px; color: #555; margin-bottom: 3px;">Subtotal: <strong>$' + unadjustedTotal.toFixed(2) + '</strong></div>';

                if (adjustment.type === 'percent_discount') {
                    html += `<div style="font-size: 13px; color: #e74c3c;">Discount (${adjustment.value}%): <strong>-$${adjustmentAmount.toFixed(2)}</strong></div>`;
                } else if (adjustment.type === 'dollar_discount') {
                    html += `<div style="font-size: 13px; color: #e74c3c;">Discount: <strong>-$${adjustmentAmount.toFixed(2)}</strong></div>`;
                } else if (adjustment.type === 'set_total') {
                    const sign = adjustmentAmount >= 0 ? '-' : '+';
                    html += `<div style="font-size: 13px; color: ${adjustmentAmount >= 0 ? '#e74c3c' : '#27ae60'};">Adjustment: <strong>${sign}$${Math.abs(adjustmentAmount).toFixed(2)}</strong></div>`;
                }

                html += '</div>';
            }

            html += '</div>';

            // === TOTAL ===
            html += `
                <div style="border-top: 2px solid #345475; margin-top: 10px; padding-top: 10px;">
                    <div class="charge-detail-row" style="font-size: 20px; font-weight: 700; display: flex; justify-content: space-between; align-items: center;">
                        <span>Total:</span>
                        <div style="display: flex; align-items: center; gap: 10px;">
                            <span style="color: #27ae60;">$${finalPrice.toFixed(2)}</span>
            `;

            // Show small "Edit" button next to total if no adjustment is active and panel is hidden
            if (adjustment.type === 'none' && !window.showAdjustmentPanel) {
                html += `<button onclick="window.toggleAdjustmentControls()" style="padding: 4px 10px; font-size: 12px; background: #3498db; color: white; border: none; border-radius: 4px; cursor: pointer;">Edit</button>`;
            }

            html += `
                        </div>
                    </div>
                </div>
            `;

            html += '</div>';
            chargeSummaryContent.innerHTML = html;
        }

        // Update other price displays
        const totalCostDisplay = document.getElementById('totalCostDisplay');
        if (totalCostDisplay) {
            totalCostDisplay.textContent = `$${finalPrice.toFixed(2)}`;
        }

        const totalCost = document.getElementById('totalCost');
        if (totalCost) {
            totalCost.textContent = `$${finalPrice.toFixed(2)}`;
            totalCost.value = finalPrice.toFixed(2);
        }

        const wizardTotalPrice = document.getElementById('wizardTotalPrice');
        if (wizardTotalPrice) {
            wizardTotalPrice.textContent = `$${finalPrice.toFixed(2)}`;
        }

        // Update adminApp if available
        if (window.adminApp && serviceKey) {
            window.adminApp.currentServiceKey = serviceKey;
            window.adminApp.finalPrice = finalPrice;
        }

        // Enable charge buttons
        const chargeButton = document.getElementById('chargeButton');
        if (chargeButton) {
            chargeButton.disabled = false;
        }

        const pricingChargeButton = document.getElementById('pricingChargeButton');
        if (pricingChargeButton) {
            pricingChargeButton.disabled = false;
        }

    } finally {
        isRunning = false;

        // Restore focus and scroll position to prevent auto-scrolling
        if (isAdjustmentInput) {
            const newAdjustmentInput = document.getElementById('adjustmentValue');
            if (newAdjustmentInput) {
                // Use setTimeout to ensure DOM is fully rendered
                setTimeout(() => {
                    newAdjustmentInput.focus();
                    if (cursorPosition !== null) {
                        newAdjustmentInput.setSelectionRange(cursorPosition, cursorPosition);
                    }
                    window.scrollTo(0, scrollPosition);
                }, 0);
            }
        }
    }
}

/**
 * Toggle adjustment controls panel visibility
 */
function toggleAdjustmentControls() {
    window.showAdjustmentPanel = !window.showAdjustmentPanel;

    // Re-run pricing calculation to update UI
    updateWizardPricing();
}

/**
 * Update adjustment type
 * @param {string} type - Adjustment type ('none', 'percent_discount', 'dollar_discount', 'set_total')
 */
function updateAdjustmentType(type) {
    if (!window.priceAdjustment) {
        window.priceAdjustment = { type: 'none', value: 0 };
    }

    window.priceAdjustment.type = type;

    // Reset value when switching to 'none'
    if (type === 'none') {
        window.priceAdjustment.value = 0;
        // Close panel when removing adjustment
        window.showAdjustmentPanel = false;
    }

    // Re-run pricing calculation
    updateWizardPricing();
}

/**
 * Update adjustment value
 * @param {number} value - Adjustment value
 */
function updateAdjustmentValue(value) {
    if (!window.priceAdjustment) {
        window.priceAdjustment = { type: 'none', value: 0 };
    }

    window.priceAdjustment.value = value;

    // Re-run pricing calculation
    updateWizardPricing();
}

/**
 * Manage minimum service fee checkbox visibility and state
 * Only shown for anodes_only service
 */
function updateMinimumFeeCheckbox(serviceKey) {
    const chargeSummary = document.querySelector('.charge-summary h3');

    // Check for existing checkbox and preserve its state
    const existingCheckbox = document.getElementById('minimumFeeCheckboxContainer');
    const existingCheckboxInput = document.getElementById('chargeMinimumFee');
    const wasChecked = existingCheckboxInput ? existingCheckboxInput.checked : false;

    if (existingCheckbox) {
        existingCheckbox.remove();
    }

    // Only show checkbox for anodes_only service
    if (serviceKey === 'anodes_only' && chargeSummary) {
        const checkedAttr = wasChecked ? 'checked' : '';
        const checkboxHtml = `
            <div id="minimumFeeCheckboxContainer" style="margin: 15px 0; padding: 12px; background: #fff3cd; border: 2px solid #ffc107; border-radius: 8px;">
                <label style="display: flex; align-items: center; cursor: pointer; font-size: 15px; font-weight: 600; color: #856404;">
                    <input
                        type="checkbox"
                        id="chargeMinimumFee"
                        ${checkedAttr}
                        onchange="window.updateWizardPricing()"
                        style="width: 20px; height: 20px; margin-right: 10px; cursor: pointer;"
                    />
                    <span>Charge $150 Minimum Service Fee</span>
                </label>
                <div style="font-size: 13px; color: #856404; margin-top: 8px; margin-left: 30px;">
                    Uncheck when returning to install anodes only (no service fee)
                </div>
            </div>
        `;

        // Insert before the h3 "Report" heading
        chargeSummary.insertAdjacentHTML('beforebegin', checkboxHtml);

        console.log('✅ Minimum fee checkbox added for anodes_only service (checked:', wasChecked, ')');
    }
}

// Expose to window
window.updateWizardPricing = updateWizardPricing;
window.toggleAdjustmentControls = toggleAdjustmentControls;
window.updateAdjustmentType = updateAdjustmentType;
window.updateAdjustmentValue = updateAdjustmentValue;
window.updateMinimumFeeCheckbox = updateMinimumFeeCheckbox;
